import {
    CHECKOUT_HIDE_INTERCOM,
    CHECKOUT_REDESIGN,
    EXPRESS_CHECKOUT_VALUE_PROPS,
    FC_PQP_MOST_POPULAR_BADGE,
    FEATURED_IN,
    FINASTERIDE_UPSELL,
    GET_STARTED_REDESIGN,
    GWP_CURRENT_PQP_CHECKOUT,
    LIBIDA_3_MONTHS_PLAN,
    LIBIDA_LP_VARIATION,
    LIBIDA_PDP,
    LIBIDA_PDP_NEW_HOW_TO_USE,
    LIBIDA_PQP_PLAN_SELECTOR_VARIANT,
    LIBIDA_PQP_PLAN_SELECTOR_VARIATION,
    LIBIDA_QUIZ_REDESIGN,
    LP_CTA_COPY_VARIANT,
    OESTRA_DETAILS_SCREEN,
    OESTRA_QUIZ_FREE_VISIT_CTA,
    OESTRA_SENSITIVE_QUESTIONS,
    OESTRA_SUPPORT_SCREEN,
    REDIRECT_TO_MENO_PQP,
    TOTAL_VALUE,
    TRUSTED_REVIEWS_COMPONENTS_VARIANT,
    TRUSTED_REVIEWS_LAYOUT_VARIANTS,
    URGENCY_WIDGET_PEOPLE_WATCHING,
} from "~/constants/flags";

export type FeatureFlagMetadata =
    | {
          type: "boolean";
          description?: string;
      }
    | {
          type: "variant";
          variants: readonly string[];
          description?: string;
      };

/**
 * Metadata for internal QA/dev tooling, not a complete feature flag registry.
 * Add entries only when a flag should be discoverable by tooling. This does not
 * affect feature flag evaluation.
 */
export const FEATURE_FLAGS_METADATA = {
    [FINASTERIDE_UPSELL]: {
        type: "boolean",
        description:
            "Preselects the finasteride formula on the BodyMatched plan page until the user changes it.",
    },
    [URGENCY_WIDGET_PEOPLE_WATCHING]: {
        type: "variant",
        variants: ["variant_a"],
        description:
            "Shows simulated viewing and purchase activity on eligible product pages.",
    },
    [OESTRA_SENSITIVE_QUESTIONS]: {
        type: "boolean",
        description:
            "Routes the Oestra quiz to the sensitive-questions survey flow.",
    },
    [OESTRA_SUPPORT_SCREEN]: {
        type: "boolean",
        description:
            "Routes the Oestra quiz to the support-screen survey flow.",
    },
    [OESTRA_DETAILS_SCREEN]: {
        type: "boolean",
        description:
            "Routes the Oestra quiz to the details-screen survey flow.",
    },
    [OESTRA_QUIZ_FREE_VISIT_CTA]: {
        type: "boolean",
        description:
            "Changes the first redesigned quiz action to “Start my free visit”.",
    },
    [LIBIDA_3_MONTHS_PLAN]: {
        type: "boolean",
        description:
            "Defaults Libida plan selection to the three-month commitment.",
    },
    [LIBIDA_PQP_PLAN_SELECTOR_VARIANT]: {
        type: "variant",
        variants: [
            LIBIDA_PQP_PLAN_SELECTOR_VARIATION.TWO_MONTH_DEFAULT,
            LIBIDA_PQP_PLAN_SELECTOR_VARIATION.THREE_MONTH_DEFAULT,
        ],
        description:
            "Uses the redesigned Libida PQP with either the two-month or three-month plan selected by default and hides legacy 20% discount badges on Libida marketing surfaces.",
    },
    [REDIRECT_TO_MENO_PQP]: {
        type: "boolean",
        description:
            "Redirects menopause quiz results to the menopause product page.",
    },
    [GET_STARTED_REDESIGN]: {
        type: "boolean",
        description:
            "Uses the redesigned getting-started and how-it-works sections.",
    },
    [FC_PQP_MOST_POPULAR_BADGE]: {
        type: "boolean",
        description:
            "Shows the most-popular badge on BodyMatched plan options.",
    },
    [GWP_CURRENT_PQP_CHECKOUT]: {
        type: "boolean",
        description:
            "Shows the Fullscript gift line item in the legacy Oestra checkout.",
    },
    [TOTAL_VALUE]: {
        type: "boolean",
        description:
            "Shows Total Value details and the alternate Oestra checkout summary.",
    },
    [LP_CTA_COPY_VARIANT]: {
        type: "variant",
        variants: [
            "see_if_you_qualify",
            "start_the_quiz",
            "check_your_eligibility",
            "get_started",
            "take_the_2_min_quiz",
            "start_my_assessment",
            "am_i_a_candidate",
            "discover_whats_right_for_me",
            "start_my_journey",
            "im_ready_to_feel_better",
            "get_my_prescription_plan",
            "start_my_free_visit",
        ],
        description: "Selects CTA copy across landing pages.",
    },
    [TRUSTED_REVIEWS_COMPONENTS_VARIANT]: {
        type: "boolean",
        description: "Uses the redesigned trusted-reviews rating components.",
    },
    [TRUSTED_REVIEWS_LAYOUT_VARIANTS]: {
        type: "variant",
        variants: ["top", "white", "yellow"],
        description:
            "Selects the trusted-reviews layout used by the Oestra quiz.",
    },
    [LIBIDA_QUIZ_REDESIGN]: {
        type: "boolean",
        description: "Switches the Libida quiz to its redesigned flow.",
    },
    [LIBIDA_LP_VARIATION]: {
        type: "boolean",
        description:
            "Uses the alternate Libida landing-page section sequence.",
    },
    [LIBIDA_PDP]: {
        type: "boolean",
        description:
            "Switches Libida pages and quiz routing to the product-detail flow.",
    },
    [LIBIDA_PDP_NEW_HOW_TO_USE]: {
        type: "boolean",
        description:
            "Uses the new How to Use section on the Libida product-detail page.",
    },
    [CHECKOUT_HIDE_INTERCOM]: {
        type: "boolean",
        description: "Hides Intercom chat during checkout billing.",
    },
    [CHECKOUT_REDESIGN]: {
        type: "variant",
        variants: ["trust-urgency-v2", "sidebar", "accordion"],
        description:
            "Selects the checkout layout; unmatched values use the standard two-step checkout.",
    },
    [EXPRESS_CHECKOUT_VALUE_PROPS]: {
        type: "variant",
        variants: [
            "Variant 1: Black button (dark version)",
            "Variant 2: White button (light version)",
        ],
        description:
            "Selects the black or white treatment for express checkout wallet buttons.",
    },
    [FEATURED_IN]: {
        type: "variant",
        variants: ["control", "black", "white"],
        description:
            "Selects the logo set and color treatment in the Featured In marquee.",
    },
} as const satisfies Readonly<Record<string, FeatureFlagMetadata>>;
